import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { DropdownItem } from 'components/dropdown/dropdown.model';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { CustomValidators } from 'weavix-shared/custom-validators';
import { Company } from '@weavix/models/src/company/company';
import { AnyBadgeEvent } from '@weavix/models/src/badges/event';
import { AnyItem, DropDownField, FieldType, Item, ItemField, ItemType } from 'weavix-shared/models/item.model';
import { Person } from 'weavix-shared/models/person.model';
import { PermissionAction } from 'weavix-shared/permissions/permissions.model';
import { AccountService } from 'weavix-shared/services/account.service';
import { AlertService, ServiceError } from 'weavix-shared/services/alert.service';
import { CompanyService } from 'weavix-shared/services/company.service';
import { ItemService } from 'weavix-shared/services/item.service';
import { PersonService } from 'weavix-shared/services/person.service';
import { ProfileService } from 'weavix-shared/services/profile.service';
import { UploadService } from 'weavix-shared/services/upload.service';
import { css } from 'weavix-shared/utils/css';
import { AutoUnsubscribe } from 'weavix-shared/utils/utils';
import { GEOFENCE_EVENT_TYPES, RowEventType } from '../../../map/weavix-map/event-log/event.service';
import { LoadingComponent } from 'components/loading/loading.component';
import { CommonModule } from '@angular/common';
import { IconComponent } from 'components/icon/icon.component';
import { TranslateModule } from '@ngx-translate/core';
import { EventLogComponent } from 'map/weavix-map/event-log/event-log.component';
import { ImageUploadComponent } from 'components/image-upload/image-upload.component';
import { DropdownComponent } from 'components/dropdown/dropdown.component';
import { InputComponent } from 'components/input/input.component';

@AutoUnsubscribe()
@Component({
    selector: 'app-item-detail',
    templateUrl: './item-detail.component.html',
    styleUrls: ['./item-detail.component.scss', '../map-detail-view.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,

        DropdownComponent,
        EventLogComponent,
        IconComponent,
        ImageUploadComponent,
        InputComponent,
        LoadingComponent,
    ],
})
export class ItemDetailComponent implements OnInit, OnDestroy, OnChanges {
    @Input() item: Item;
    @Output() closeOutput = new EventEmitter<void>();

    form: FormGroup<{
        properties: FormGroup<{
            properties: FormArray<FormGroup<{
                key: FormControl<string>;
                value: FormControl<any>;
                fieldId: FormControl<string>;
            }>>;
        }> | null;
    }> | null;
    fieldType = FieldType;
    companiesInput: DropdownItem[] = [];
    peopleInput: DropdownItem[] = [];
    hasUpdatePermission: boolean = false;
    rowEventTypes: RowEventType[] = GEOFENCE_EVENT_TYPES;
    badgeEventsHistory: AnyBadgeEvent[];
    badgeEvents: Subject<AnyBadgeEvent> = new Subject<AnyBadgeEvent>();
    css = css;
    private fieldDropdownOptionsMap = {};
    private fieldTypeMap = {};
    private formSubscription: Subscription;
    submittingChanges: boolean = false;
    isLoading: boolean = false;
    itemType: ItemType;
    get itemProperties() {
        return this.form.controls.properties.controls.properties;
    }

    constructor(
        private readonly itemService: ItemService,
        private readonly profileService: ProfileService,
        private readonly accountService: AccountService,
        private readonly companyService: CompanyService,
        private readonly peopleService: PersonService,
        private readonly alertService: AlertService,
        private readonly uploadService: UploadService,
        private readonly fb: FormBuilder,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    async ngOnInit() {
        this.hasUpdatePermission = this.profileService.hasPermission(PermissionAction.EditItems, this.item.facilityId);
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes?.item?.currentValue?.id !== changes?.item?.previousValue?.id) {
            this.isLoading = true;
            await this.setItemType(this.item);
            await this.getFormDataAndBuildForm();
            this.isLoading = false;
            this.cdr.markForCheck();
        }
    }

    ngOnDestroy() {
        this.formSubscription?.unsubscribe();
    }

    private async setItemType(item: Item) {
        this.itemType = await this.itemService.getType(this, item.typeId);
    }

    private async getFormDataAndBuildForm(): Promise<void> {
        if (!this.companiesInput.length) await this.loadCompanies();
        if (!this.peopleInput.length) await this.loadPeople();
        this.buildForm();
    }

    private async submitFormChanges(): Promise<void> {
        if (this.form.invalid) return;
        this.submittingChanges = true;
        const formValues = this.form.value;
        try {
            const request: AnyItem = {
                name: this.item.name,
                typeId: this.item.typeId,
                itemProperties: (formValues.properties.properties || []).map(p => ({ value: p.value, fieldId: p.fieldId })),
                image: this.item?.image ?? null,
            };

            await this.itemService.updateItem(this, this.item.id, request);
        } catch (e) {
            this.alertService.sendServiceError(e, ServiceError.Update, 'shared.items.items');
        } finally {
            this.submittingChanges = false;
            this.cdr.markForCheck();
        }
    }

    async handleImageChange(value?: { src: string, file: File }): Promise<void> {
        try {
            if (value?.file) {
                const image = await this.uploadService.upload(this, value.file);
                this.item.image = image.uri;
            } else {
                this.item.image = null;
            }
            await this.submitFormChanges();
        } catch (e) {
            this.alertService.sendServiceError(e, ServiceError.Update, 'shared.items.items');
        } finally {
            this.cdr.markForCheck();
        }
    }

    private async loadCompanies() {
        try {
            const account = await this.accountService.getAccount();
            const companies: Company[] = await this.companyService.getAllOnAccount(this);
            this.companiesInput = companies
                .map(company => ({ label: company.name, key: company.id, imgUrl: company?.logo }))
                .concat([{ label: account.name, key: account.id, imgUrl: account?.image }]);
        } catch (e) {
            console.error(e);
            this.alertService.sendServiceError(e, ServiceError.Get, 'shared.company.companies');
        }
    }

    private async loadPeople() {
        try {
            const people: Person[] = await this.peopleService.getPeople(this);
            this.peopleInput = people.map(p => ({ label: p.fullName, key: p.id, imgUrl: p.avatarFile }));
        } catch (e) {
            console.error(e);
            this.alertService.sendServiceError(e, ServiceError.Get, 'shared.person.people');
        }
    }

    private buildForm() {
        if (this.formSubscription) {
            this.formSubscription?.unsubscribe();
            this.formSubscription = null;
        }

        this.form = null;

        let propertiesSection: FormGroup = null;

        if (this.itemType?.fields) {
            const properties = this.itemType.fields.map(field => this.getAndSetupItemPropertyFields(field, this.item));
            propertiesSection = this.fb.group({ properties: this.fb.array(properties) });
        }

        this.form = this.fb.group({
            properties: propertiesSection,
        });

        this.formSubscription = this.form.valueChanges
            .pipe(debounceTime(1000), distinctUntilChanged())
            .subscribe(() => this.submitFormChanges());
    }

    private getAndSetupItemPropertyFields(field: ItemField, itemData: Item): FormGroup {
        const getItemPropValidator = () => {
            const validators: ValidatorFn[] = [];
            if (field.type === FieldType.Number) validators.push(CustomValidators.anyNumber);
            return validators;
        };

        const getDropdownOptions = (option: string): DropdownItem => ({
            key: option,
            label: option,
        });

        this.fieldTypeMap[field.fieldId] = field.type;
        if (field.type === FieldType.Dropdown) {
            const dropdownField = field as DropDownField;
            const options = dropdownField.options || [];
            this.fieldDropdownOptionsMap[field.fieldId] = options.map(o => getDropdownOptions(o));
        }

        const itemProperty = (itemData?.itemProperties ?? []).find(p => p.fieldId === field.fieldId);
        return this.fb.group({
            key: this.fb.control(field.display),
            value: this.fb.control({ value: itemProperty?.value ?? '', disabled: !this.hasUpdatePermission }, getItemPropValidator()),
            fieldId: this.fb.control(field.fieldId),
        });
    }

    getFieldOptions(fieldId) {
        return this.fieldDropdownOptionsMap[fieldId];
    }

    getFieldType(fieldId) {
        return this.fieldTypeMap[fieldId];
    }

    handleClose() {
        this.closeOutput.emit();
    }
}
